/**
 * Level definitions. Mirroring negates only the scenario layout's lateral axis.
 *
 * Per the gameplay-rework spec (ADR-017): no static obstacles anywhere in the
 * playable corridor — the research encounter contains exactly one alley, one
 * robot, one approaching pedestrian, and one destination; calibration levels
 * teach steering with offset markers instead of obstacles/gates, so there are
 * no narrow gaps or chokepoints.
 */

import {
  ALLEY_HALF_WIDTH,
  DESTINATION_LINE_HALF_WIDTH,
  DESTINATION_RADIUS,
  PED_SPAWN_Z,
  RESEARCH_ALLEY_LENGTH,
  RESEARCH_DESTINATION_Z,
  TIMEOUT_S,
} from "./constants";
import { compileMap } from "./map/compile";
import { MISSION_TIMEOUT_S, buildMission, type MissionBuild } from "./mission";
import { CrossingPedestrian, ResearchPedestrian, type PedestrianModel } from "./pedestrian";
import { treeObstacles } from "./trees";
import type { ControlScheme, Obstacle, ScenarioConfig, WorldDef } from "./types";

/** cal3 crossing pedestrian: lateral path endpoints and trigger. */
export const CAL3_CROSS_Z = 6;
export const CAL3_CROSS_X = 0.8;
export const CAL3_TRIGGER_Z = 2;

export interface LevelDef {
  id: ScenarioConfig["level"];
  name: string;
  hint: string;
  world: WorldDef;
  robotSpawn: { x: number; z: number; heading: number };
  /** z of the level's focus point; used for calibration phase transitions. */
  hazardZ: number;
  isResearch: boolean;
  /** Attempt hard timeout (s). */
  timeoutS: number;
  /** The one-minute delivery mission carries its route/agents/encounters. */
  mission?: MissionBuild;
  /** Mission-only control/safety semantics (ADR-022); lab levels keep
   * ADR-017 behaviour byte-for-byte. */
  features: LevelFeatures;
  makePedestrian(): PedestrianModel | null;
}

export interface LevelFeatures {
  /** Default control model (ADR-023): the mission drives like a racing game,
   * the lab levels keep the ADR-017 heading stick their pins depend on. */
  steering: ControlScheme;
  /** Road-relative control frame, latched turns, pivot-rate, yaw brake cap. */
  frameControl: boolean;
  /** Mission safety semantics (rear hazards ignored, inside-not-closing, wider clear). */
  missionSafety: boolean;
  /** Living Safe Corridor (ADR-065): blue band + amber paths + speed-by-length. */
  corridor: boolean;
}

const LAB_FEATURES: LevelFeatures = { steering: "heading", frameControl: false, missionSafety: false, corridor: false };
const MISSION_FEATURES: LevelFeatures = { steering: "rate", frameControl: true, missionSafety: true, corridor: true };

function baseWorld(
  length: number,
  dest: { x: number; z: number },
  obstacles: Obstacle[] = [],
): WorldDef {
  return {
    alleyHalfWidth: ALLEY_HALF_WIDTH,
    alleyLength: length,
    destination: dest,
    destinationRadius: DESTINATION_RADIUS,
    destinationLineHalfWidth: DESTINATION_LINE_HALF_WIDTH,
    obstacles,
  };
}

export function buildLevel(cfg: ScenarioConfig): LevelDef {
  const m: 1 | -1 = cfg.mirror ? -1 : 1;

  switch (cfg.level) {
    case "cal1":
      return {
        id: "cal1",
        name: "Calibration 1 — Steer",
        hint: "Steer to the marker.",
        world: baseWorld(14, { x: -0.5 * m, z: 10 }),
        robotSpawn: { x: 0, z: 0, heading: 0 },
        hazardZ: 7,
        isResearch: false,
        timeoutS: TIMEOUT_S,
        features: LAB_FEATURES,
        makePedestrian: () => null,
      };

    case "cal2":
      return {
        id: "cal2",
        name: "Calibration 2 — Weave",
        hint: "Now the marker is on the other side.",
        world: baseWorld(14, { x: 0.5 * m, z: 10 }),
        robotSpawn: { x: 0, z: 0, heading: 0 },
        hazardZ: 7,
        isResearch: false,
        timeoutS: TIMEOUT_S,
        features: LAB_FEATURES,
        makePedestrian: () => null,
      };

    case "cal3":
      return {
        id: "cal3",
        name: "Calibration 3 — Yield",
        hint: "Release the joystick to yield.",
        world: baseWorld(14, { x: 0, z: 11 }),
        robotSpawn: { x: 0, z: 0, heading: 0 },
        hazardZ: CAL3_CROSS_Z,
        isResearch: false,
        timeoutS: TIMEOUT_S,
        features: LAB_FEATURES,
        makePedestrian: () =>
          new CrossingPedestrian(m, CAL3_CROSS_Z, CAL3_CROSS_X, CAL3_TRIGGER_Z),
      };

    case "research":
      return {
        id: "research",
        name: "Alley Encounter",
        hint: "Reach the marker.",
        world: baseWorld(RESEARCH_ALLEY_LENGTH, { x: 0, z: RESEARCH_DESTINATION_Z }),
        robotSpawn: { x: 0, z: 0, heading: 0 },
        hazardZ: PED_SPAWN_Z / 2,
        isResearch: true,
        timeoutS: TIMEOUT_S,
        features: LAB_FEATURES,
        makePedestrian: () => new ResearchPedestrian(cfg.variant, m, cfg.seed),
      };

    case "mission": {
      // A custom map compiles to the same MissionBuild the district does,
      // so everything downstream — frame, safety, guidance, telemetry —
      // behaves identically. Only the geometry and the cast differ.
      const mission = cfg.map ? compileMap(cfg.map, cfg) : buildMission(cfg);
      // A delivery starts at the start. Free roam has no errand, so the start of
      // the route is just one end of a reconstructed street: drive forward and
      // you leave the photographs behind, which is what makes a map look broken
      // at its far end. Put the robot half way along instead, where there is
      // scene in both directions.
      const start = mission.path.point(cfg.freeRoam ? mission.path.length/2 : 0);
      // Two-stage-left crossing legs, from the route resolver's anchor pairs
      // (`X` pivots, `X__xend` ends the zebra leg — see hookLeftTurn).
      const crossings = mission.path
        .anchorList()
        .filter(([n]) => n.endsWith("__xend"))
        .map(([n, s1]) => ({ s0: mission.path.anchorOrNull(n.slice(0, -"__xend".length)) ?? s1, s1 }))
        .filter((c) => c.s1 > c.s0)
        .sort((a, b) => a.s0 - b.s0);
      const world: WorldDef = {
        alleyHalfWidth: 1.8, // unused: curbs are the road network (ADR-022)
        alleyLength: mission.path.length,
        destination: mission.destination,
        destinationRadius: 0.9,
        destinationLineHalfWidth: 1.6,
        crossings,
        obstacles: [...mission.obstacles, ...treeObstacles(mission.network, mission.decor, mission.mirror)],
        network: mission.network,
        path: mission.path,
      };
      return {
        id: "mission",
        name: cfg.map ? cfg.map.name : "Delivery Run",
        hint: cfg.map?.description ?? "Follow the signs to the customer.",
        world,
        robotSpawn: { x: start.x, z: start.z, heading: start.theta },
        hazardZ: mission.path.length / 2,
        isResearch: false,
        timeoutS: cfg.map?.timeoutS ?? MISSION_TIMEOUT_S,
        features: MISSION_FEATURES,
        mission,
        makePedestrian: () => null,
      };
    }
  }
}
